/**
 * RESPONSIBILITY: the home page's opening band.
 *
 * Its own section, like every other band: one pencil where the band is, and editing the
 * hero is not the same save as editing the give card three screens down.
 *
 * KEPT TO WHAT AN EDITOR CAN ACT ON. A video, or a picture show - and for the show, large
 * pictures and small ones. How the show moves, in what order, how long each stays and how
 * many go side by side are the site's job, not a setting.
 *
 * No @EditableBy, so WRITE defaults to NONE — admin only.
 */
import { Section, Text, Paragraph, Img, Video, Link, List, Bool, Choice } from '@framework/core/decorators';
import type { ImageValue } from '@framework/core';
import { SectionName } from '../section-names';
import { HeroStill } from './rows/still';
import { HeroGroupPicture } from './rows/group-picture';
import { HERO_MODE, HERO_MODES, DEFAULT_HERO_MODE } from './rows/hero-mode';
import { LINK_HINT } from '../hints';

const BACKDROP = 'Behind the title';

@Section({ name: SectionName.HomeHero, label: 'Home · Hero' })
export class HomeHeroSection {
  @Choice({
    label: 'What plays', options: HERO_MODES, default: DEFAULT_HERO_MODE, as: 'radio', group: BACKDROP,
    description:
      'Video: plays the video below, and the pictures show while it loads. '
      + 'Picture show: plays the pictures like a film, on its own.',
  })
  mode!: string;

  @Img({
    label: 'Large picture', folder: 'home', maxSizeMb: 12, size: '1920 × 1080', group: BACKDROP,
    description: 'Shown first.',
  })
  image!: ImageValue;

  @List({
    label: 'More large pictures', of: () => HeroStill, maxItems: 16, group: BACKDROP,
    description: 'Shown one after another with the one above.',
  })
  stills!: HeroStill[];

  @List({
    label: 'Small pictures', of: () => HeroGroupPicture, maxItems: 24, group: BACKDROP,
    showWhen: { field: 'mode', is: [HERO_MODE.Show] },
    description: 'Shown two or three together on larger screens, whole. Use the size given. Optional.',
  })
  groups!: HeroGroupPicture[];

  @Video({
    label: 'Video', folder: 'home', maxSizeMb: 24, size: '1920 × 1080', group: BACKDROP,
    showWhen: { field: 'mode', is: [HERO_MODE.Video] },
    description: 'No sound is played. The pictures above show until it has loaded.',
  })
  video!: ImageValue;

  /* No default on either, deliberately. Clearing a box saves the field as absent, and an
     absent field is filled from its default on the way back — so a default here made the
     line impossible to remove: empty it, save, and it came straight back. */
  @Text({
    label: 'Eyebrow', maxLength: 120,
    description: 'The small line above the title. Leave empty for none.',
  })
  eyebrow!: string;

  @Text({
    label: 'Title', maxLength: 300,
    description: 'The large headline. Leave empty for none — the picture then speaks on its own.',
  })
  title!: string;

  @Paragraph({
    label: 'Tagline', maxLength: 300, rows: 2,
    description: 'One short line under the title — the long story belongs on the About page.',
  })
  tagline!: string;

  /**
   * One switch for both, and the words are kept while it is off — so hiding the buttons
   * is a tick box, not deleting two labels somebody then has to remember. On unless
   * somebody says otherwise, read as `!== false` like every other switch here.
   */
  @Bool({
    label: 'Show the buttons', default: true, group: 'Buttons',
    description: 'Untick to hide both buttons. Their text and links are kept for when you tick it again.',
  })
  showButtons!: boolean;

  @Text({ label: 'First button text', maxLength: 120, default: 'Visit', group: 'Buttons' })
  ctaLabel!: string;
  @Link({ label: 'First button link', group: 'Buttons', description: LINK_HINT })
  ctaHref!: string;
  @Text({ label: 'Second button text', maxLength: 120, default: 'Get involved', group: 'Buttons' })
  altLabel!: string;
  @Link({ label: 'Second button link', group: 'Buttons', description: LINK_HINT })
  altHref!: string;
}
